import {
    MagnifyingGlassIcon,
    PlusIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Center,
    Group,
    Loader,
    Modal,
    Pagination,
    Paper,
    rem,
    ScrollArea,
    Stack,
    Switch,
    Table,
    Text,
    Textarea,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    EmployeeDictionaryEntryDto,
    EmployeeDictionaryType,
    UpdateDictionaryEntryRequest,
} from '../../../models/employeeHub';
import { useAddDictionaryEntry } from '../hooks/useAddDictionaryEntry';
import { useEmployeeDictionary } from '../../../hooks/queries/useEmployeeDictionary';
import { useUpdateDictionaryEntry } from '../hooks/useUpdateDictionaryEntry';

const ITEMS_PER_PAGE = 50;

// Stable reference so the useQuery destructure default doesn't create a new
// array on every render while loading (that would re-trigger effects that
// depend on it on every commit — an infinite render loop).
const EMPTY_ENTRIES: EmployeeDictionaryEntryDto[] = [];

interface EmployeeDictionaryManagementProps {
    type: EmployeeDictionaryType;
    title: string;
    searchPlaceholder?: string;
}

interface EmployeeDictionaryRowProps {
    entry: EmployeeDictionaryEntryDto;
    isSaving: boolean;
    onSave: (id: number, payload: UpdateDictionaryEntryRequest) => void;
}

// Each row owns its own edit-in-progress state. Typing in one row's TextInput
// only re-renders this component, not the whole (potentially 50-row) table —
// that isolation is what keeps typing responsive.
const EmployeeDictionaryRow: React.FC<EmployeeDictionaryRowProps> = React.memo(
    ({ entry, isSaving, onSave }) => {
        const [displayName, setDisplayName] = useState(entry.displayName);
        const [isActive, setIsActive] = useState(entry.isActive);

        // If the underlying entry changes (e.g. a refetch after a save elsewhere),
        // resync local draft state to the new server values.
        useEffect(() => {
            setDisplayName(entry.displayName);
            setIsActive(entry.isActive);
        }, [entry.displayName, entry.isActive]);

        const isDirty = displayName !== entry.displayName || isActive !== entry.isActive;
        const trimmedDisplayName = displayName.trim();

        return (
            <Table.Tr style={{ opacity: isActive ? 1 : 0.5 }}>
                <Table.Td style={{ maxWidth: rem(110) }}>
                    <Tooltip label={entry.code} openDelay={300} disabled={entry.code.length <= 12}>
                        <Text ff="monospace" size="sm" c="dimmed" truncate="end">
                            {entry.code}
                        </Text>
                    </Tooltip>
                </Table.Td>
                <Table.Td>
                    <Textarea
                        value={displayName}
                        onChange={(event) => setDisplayName(event.currentTarget.value)}
                        aria-label={`Display name for ${entry.code}`}
                        autosize
                        minRows={1}
                        maxRows={4}
                    />
                </Table.Td>
                <Table.Td>
                    <Switch
                        checked={isActive}
                        onChange={(event) => setIsActive(event.currentTarget.checked)}
                        aria-label={`Active toggle for ${entry.code}`}
                    />
                </Table.Td>
                <Table.Td>
                    <Button
                        size="xs"
                        variant="light"
                        disabled={!isDirty || !trimmedDisplayName}
                        loading={isSaving}
                        onClick={() => onSave(entry.id, { displayName: trimmedDisplayName, isActive })}
                    >
                        Save
                    </Button>
                </Table.Td>
            </Table.Tr>
        );
    }
);
EmployeeDictionaryRow.displayName = 'EmployeeDictionaryRow';

export const EmployeeDictionaryManagement: React.FC<EmployeeDictionaryManagementProps> = ({
    type,
    title,
    searchPlaceholder = 'Search by code or display name...',
}) => {
    const { data: entries = EMPTY_ENTRIES, isLoading, isError } = useEmployeeDictionary(type);
    const updateMutation = useUpdateDictionaryEntry(type);
    const addMutation = useAddDictionaryEntry(type);

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);
    const [currentPage, setCurrentPage] = useState(1);
    const [savingId, setSavingId] = useState<number | null>(null);

    const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
    const [newCode, setNewCode] = useState('');
    const [newDisplayName, setNewDisplayName] = useState('');
    const [addError, setAddError] = useState<string | null>(null);

    const filteredEntries = useMemo(() => {
        if (!debouncedSearchQuery.trim()) {
            return entries;
        }
        const query = debouncedSearchQuery.toLowerCase();
        return entries.filter(
            (entry) =>
                entry.code.toLowerCase().includes(query) ||
                entry.displayName.toLowerCase().includes(query)
        );
    }, [entries, debouncedSearchQuery]);

    const totalPages = Math.max(1, Math.ceil(filteredEntries.length / ITEMS_PER_PAGE));
    const paginatedEntries = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredEntries.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredEntries, currentPage]);

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        setCurrentPage(1);
    };

    const handleSaveRow = useCallback(
        (id: number, payload: UpdateDictionaryEntryRequest) => {
            setSavingId(id);
            updateMutation.mutate(
                { id, payload },
                {
                    onSuccess: () => {
                        notifications.show({
                            title: 'Saved',
                            message: `"${payload.displayName}" updated successfully`,
                            color: 'green',
                        });
                    },
                    onError: () => {
                        notifications.show({
                            title: 'Error',
                            message: 'Failed to update entry',
                            color: 'red',
                        });
                    },
                    onSettled: () => {
                        setSavingId(null);
                    },
                }
            );
        },
        // updateMutation.mutate is the stable reference; the wrapping object from
        // useMutation is re-created every render and would defeat memoization here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [updateMutation.mutate]
    );

    const handleAdd = () => {
        const code = newCode.trim();
        const displayName = newDisplayName.trim();
        setAddError(null);

        if (!code || !displayName) {
            setAddError('Code and display name are both required.');
            return;
        }

        addMutation.mutate(
            { code, displayName },
            {
                onSuccess: () => {
                    notifications.show({
                        title: 'Added',
                        message: `"${displayName}" added successfully`,
                        color: 'green',
                    });
                    setNewCode('');
                    setNewDisplayName('');
                    closeAddModal();
                },
                onError: (error) => {
                    const isConflict = error instanceof Error && error.message.includes('409');
                    setAddError(
                        isConflict
                            ? `Code "${code}" already exists.`
                            : 'Failed to add entry.'
                    );
                },
            }
        );
    };

    if (isLoading) {
        return (
            <Center py="xl">
                <Loader size="lg" />
            </Center>
        );
    }

    if (isError) {
        return (
            <Alert color="red" title="Error">
                Failed to load {title.toLowerCase()}.
            </Alert>
        );
    }

    return (
        <Stack gap="lg">
            <Group justify="space-between" align="flex-end">
                <TextInput
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={(event) => handleSearchChange(event.currentTarget.value)}
                    leftSection={<MagnifyingGlassIcon style={{ width: rem(18) }} />}
                    rightSection={
                        searchQuery ? (
                            <ActionIcon onClick={() => handleSearchChange('')} variant="subtle" size="sm">
                                <XMarkIcon style={{ width: rem(16) }} />
                            </ActionIcon>
                        ) : null
                    }
                    style={{ flex: 1, maxWidth: 400 }}
                    aria-label={searchPlaceholder}
                />
                <Button leftSection={<PlusIcon style={{ width: rem(16) }} />} onClick={openAddModal}>
                    Add {title.replace(/s$/, '')}
                </Button>
            </Group>

            <Group>
                <Badge size="lg" variant="outline">
                    Total: {entries.length}
                </Badge>
                {searchQuery && (
                    <Badge size="lg" variant="outline" color="blue">
                        Filtered: {filteredEntries.length}
                    </Badge>
                )}
            </Group>

            <Paper withBorder>
                <ScrollArea>
                    <Table striped highlightOnHover layout="fixed">
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th >Code</Table.Th>
                                <Table.Th>Display Name</Table.Th>
                                <Table.Th style={{ width: rem(80) }}>Active</Table.Th>
                                <Table.Th style={{ width: rem(80) }}>Actions</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {paginatedEntries.length === 0 ? (
                                <Table.Tr>
                                    <Table.Td colSpan={4}>
                                        <Center py="xl">
                                            <Text c="dimmed">
                                                {searchQuery ? 'No entries match your search' : `No ${title.toLowerCase()} found`}
                                            </Text>
                                        </Center>
                                    </Table.Td>
                                </Table.Tr>
                            ) : (
                                paginatedEntries.map((entry) => (
                                    <EmployeeDictionaryRow
                                        key={entry.id}
                                        entry={entry}
                                        isSaving={savingId === entry.id}
                                        onSave={handleSaveRow}
                                    />
                                ))
                            )}
                        </Table.Tbody>
                    </Table>
                </ScrollArea>
            </Paper>

            {totalPages > 1 && (
                <Group justify="center">
                    <Pagination total={totalPages} value={currentPage} onChange={setCurrentPage} size="sm" />
                </Group>
            )}

            <Modal
                opened={addModalOpened}
                onClose={() => {
                    closeAddModal();
                    setAddError(null);
                }}
                title={`Add ${title.replace(/s$/, '')}`}
            >
                <Stack>
                    <TextInput
                        label="Code"
                        placeholder="Enter code"
                        value={newCode}
                        onChange={(event) => setNewCode(event.currentTarget.value)}
                        required
                    />
                    <Textarea
                        label="Display Name"
                        placeholder="Enter display name"
                        value={newDisplayName}
                        onChange={(event) => setNewDisplayName(event.currentTarget.value)}
                        required
                        autosize
                        minRows={1}
                        maxRows={4}
                    />
                    {addError && (
                        <Alert color="red" variant="light">
                            {addError}
                        </Alert>
                    )}
                    <Group justify="flex-end" mt="md">
                        <Button
                            variant="default"
                            onClick={() => {
                                closeAddModal();
                                setAddError(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleAdd} loading={addMutation.isPending}>
                            Add
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
};
